import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["submitButton", "spinner", "fileInput", "uploadStatus", "formFields"]

  connect() {
    // Listen for form submission on the controller element itself
    this.boundSubmitStart = this.handleSubmitStart.bind(this)
    this.boundSubmitEnd = this.handleSubmitEnd.bind(this)
    
    this.element.addEventListener("turbo:submit-start", this.boundSubmitStart)
    this.element.addEventListener("turbo:submit-end", this.boundSubmitEnd)

    // Listen for file selection to show preview
    if (this.hasFileInputTarget) {
      this.fileInputTarget.addEventListener("change", this.handleFileSelect.bind(this))
    }
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-start", this.boundSubmitStart)
    this.element.removeEventListener("turbo:submit-end", this.boundSubmitEnd)
  }

  handleFileSelect(event) {
    const file = event.target.files[0]
    if (file) {
      const fileSize = (file.size / (1024 * 1024)).toFixed(2)
      const fileName = file.name
      const fileType = file.name.toLowerCase().endsWith('.pdf') ? 'PDF' : 'DOCX'

      // Check file size limit (50MB)
      if (file.size > 50 * 1024 * 1024) {
        this.updateStatus(t("javascript.controllers.compliance_upload_controller.file_too_large", { name: fileName, size: fileSize }), "error")
        event.target.value = '' // Clear the input
        return
      }

      this.updateStatus(t("javascript.controllers.compliance_upload_controller.ready_to_upload", { name: fileName, size: fileSize, type: fileType }), "info")
    }
  }

  handleSubmitStart(event) {
    // Disable submit button
    if (this.hasSubmitButtonTarget) {
      this._originalButtonHTML = this.submitButtonTarget.innerHTML
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        ${t("javascript.controllers.compliance_upload_controller.saving")}
      `
    }

    // Show spinner
    this.showSpinner()
    this.updateStatus(t("javascript.controllers.compliance_upload_controller.saving_document"), "loading")

    // Optionally disable form fields
    if (this.hasFormFieldsTarget) {
      const inputs = this.formFieldsTarget.querySelectorAll("input, select, textarea")
      inputs.forEach(input => input.disabled = true)
    }
  }

  handleSubmitEnd(event) {
    const { success, fetchResponse } = event.detail

    if (success && fetchResponse.response.ok) {
      this.updateStatus(t("javascript.controllers.compliance_upload_controller.save_complete_redirecting"), "success")

      // The server will handle the redirect via turbo or HTML response
      // Keep the spinner visible during redirect
    } else {
      // Error occurred
      this.updateStatus(t("javascript.controllers.compliance_upload_controller.save_failed"), "error")
      this.hideSpinner()

      // Re-enable submit button
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
        this.submitButtonTarget.innerHTML = this._originalButtonHTML || t("javascript.controllers.compliance_upload_controller.upload_document")
      }

      // Re-enable form fields
      if (this.hasFormFieldsTarget) {
        const inputs = this.formFieldsTarget.querySelectorAll("input, select, textarea")
        inputs.forEach(input => input.disabled = false)
      }
    }
  }

  showSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.remove("hidden")
    }
  }

  hideSpinner() {
    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.add("hidden")
    }
  }

  updateStatus(message, type = "info") {
    if (this.hasUploadStatusTarget) {
      this.uploadStatusTarget.textContent = message
      this.uploadStatusTarget.classList.remove("hidden", "text-gray-400", "text-blue-400", "text-green-400", "text-red-400")

      switch(type) {
        case "loading":
          this.uploadStatusTarget.classList.add("text-blue-400")
          break
        case "success":
          this.uploadStatusTarget.classList.add("text-green-400")
          break
        case "error":
          this.uploadStatusTarget.classList.add("text-red-400")
          break
        default:
          this.uploadStatusTarget.classList.add("text-gray-400")
      }
    }
  }
}
